import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Clock,
  DollarSign,
  XCircle,
  User,
  Calendar,
  Euro,
  FileText,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Settings,
  CheckSquare
} from 'lucide-react';
import commissionService from '@/services/commissionService';
import type { CommissionPeriod, CommissionDetail } from '@/types/commission';
import { CommissionStatus, formatCurrency, formatPeriod, getCommissionStatusLabel, getCommissionStatusColor } from '@/types/commission';
import { cn } from '@/utils/cn';

export default function CommissionValidationPage() {
  const { user } = useAuthStore();
  const location = useLocation();
  const [periods, setPeriods] = useState<CommissionPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CommissionStatus | 'all'>('all');
  const [expandedPeriod, setExpandedPeriod] = useState<number | null>(null);
  const [periodDetails, setPeriodDetails] = useState<{ [key: number]: CommissionDetail[] }>({});
  const [loadingDetails, setLoadingDetails] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [validationNotes, setValidationNotes] = useState<{ [key: number]: string }>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isSuperAdmin = user?.role === 'super_admin';

  const navigationTabs = [
    { name: 'Dashboard', href: '/commissions/dashboard', icon: BarChart3, current: location.pathname === '/commissions/dashboard' },
    ...(isSuperAdmin ? [{ name: 'Configuration', href: '/commissions/config', icon: Settings, current: location.pathname === '/commissions/config' }] : []),
    ...(isSuperAdmin ? [{ name: 'Validation', href: '/commissions/validation', icon: CheckSquare, current: location.pathname === '/commissions/validation' }] : [])
  ];

  useEffect(() => {
    loadPeriods();
  }, [statusFilter]);

  const loadPeriods = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: { status?: CommissionStatus } = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const data = await commissionService.listPeriods(params);
      setPeriods(data);
    } catch (err: any) {
      console.error('Error loading periods:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des périodes');
    } finally {
      setLoading(false);
    }
  };

  const loadPeriodDetails = async (periodId: number) => {
    if (periodDetails[periodId]) {
      // Already loaded
      return;
    }
    try {
      setLoadingDetails(periodId);
      const details = await commissionService.getPeriodDetails(periodId);
      setPeriodDetails(prev => ({ ...prev, [periodId]: details }));
    } catch (err: any) {
      console.error('Error loading period details:', err);
    } finally {
      setLoadingDetails(null);
    }
  };

  const toggleExpand = async (periodId: number) => {
    if (expandedPeriod === periodId) {
      setExpandedPeriod(null);
    } else {
      setExpandedPeriod(periodId);
      await loadPeriodDetails(periodId);
    }
  };

  const handleValidate = async (periodId: number) => {
    try {
      setActionLoading(periodId);
      setError(null);
      await commissionService.validatePeriod(periodId, {
        validation_notes: validationNotes[periodId] || undefined
      });
      setSuccessMessage('Période validée avec succès');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadPeriods();
    } catch (err: any) {
      console.error('Error validating period:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePay = async (periodId: number) => {
    try {
      setActionLoading(periodId);
      setError(null);
      await commissionService.payPeriod(periodId, {
        payment_notes: validationNotes[periodId] || undefined
      });
      setSuccessMessage('Période marquée comme payée');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadPeriods();
    } catch (err: any) {
      console.error('Error paying period:', err);
      setError(err.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (periodId: number) => {
    const reason = validationNotes[periodId];
    if (!reason) {
      setError('Veuillez indiquer une raison pour l\'annulation');
      return;
    }
    try {
      setActionLoading(periodId);
      setError(null);
      await commissionService.cancelPeriod(periodId, { reason });
      setSuccessMessage('Période annulée');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadPeriods();
    } catch (err: any) {
      console.error('Error cancelling period:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusIcon = (status: CommissionStatus) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'validated':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case 'paid':
        return <DollarSign className="h-4 w-4 text-green-500" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const statusOptions: { value: CommissionStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'Tous les statuts' },
    { value: CommissionStatus.PENDING, label: 'En attente' },
    { value: CommissionStatus.VALIDATED, label: 'Validées' },
    { value: CommissionStatus.PAID, label: 'Payées' },
    { value: CommissionStatus.CANCELLED, label: 'Annulées' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des périodes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Validation des Commissions</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Validez et payez les commissions des apporteurs d'affaires
          </p>
        </div>
        <button
          onClick={loadPeriods}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      {/* Navigation Tabs */}
      {navigationTabs.length > 1 && (
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {navigationTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Link
                  key={tab.name}
                  to={tab.href}
                  className={cn(
                    'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm',
                    tab.current
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  )}
                >
                  <Icon className="mr-2 h-5 w-5" />
                  {tab.name}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800 flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            {successMessage}
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Filtrer par statut:
        </label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as CommissionStatus | 'all')}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          {statusOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Periods List */}
      {periods.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Aucune période de commission
            </h3>
            <p className="text-gray-600">
              {statusFilter === 'all'
                ? 'Aucune période de commission n\'a été calculée.'
                : `Aucune période avec le statut "${statusOptions.find(o => o.value === statusFilter)?.label}".`
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {periods.map(period => (
            <Card key={period.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(period.status)}
                      <Badge variant={getCommissionStatusColor(period.status) as any}>
                        {getCommissionStatusLabel(period.status)}
                      </Badge>
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {period.admin_name || `Apporteur #${period.admin_id}`}
                      </CardTitle>
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <Calendar className="h-3 w-3" />
                        {formatPeriod(period.period_start)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(period.commission_amount)}
                    </div>
                    <p className="text-sm text-gray-500">
                      {period.total_orders} commandes • {formatCurrency(period.total_sales)} CA
                    </p>
                    <p className="text-xs text-gray-400">
                      Taux: {period.commission_rate}%
                      {period.bonus_applied && (
                        <span className="ml-2 text-green-600 font-medium">
                          🎉 Bonus +{period.bonus_rate}%
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                {/* Expand/Collapse Button */}
                <button
                  onClick={() => toggleExpand(period.id)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                  {expandedPeriod === period.id ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      Masquer les détails
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      Voir les détails ({period.total_orders} factures)
                    </>
                  )}
                </button>

                {/* Expanded Details */}
                {expandedPeriod === period.id && (
                  <div className="mt-4 space-y-4">
                    {/* Invoice Details */}
                    {loadingDetails === period.id ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    ) : periodDetails[period.id] && periodDetails[period.id].length > 0 ? (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Facture</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Client</th>
                              <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Montant</th>
                              <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">Commission</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Date paiement</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {periodDetails[period.id].map(detail => (
                              <tr key={detail.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td className="px-4 py-2 flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-gray-400" />
                                  #{detail.invoice_id}
                                </td>
                                <td className="px-4 py-2">{detail.buyer_name || `Client #${detail.client_id}`}</td>
                                <td className="px-4 py-2 text-right">{formatCurrency(detail.invoice_amount)}</td>
                                <td className="px-4 py-2 text-right font-medium text-green-600">{formatCurrency(detail.commission_amount)}</td>
                                <td className="px-4 py-2">{new Date(detail.paid_date).toLocaleDateString('fr-FR')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-4">Aucun détail disponible</p>
                    )}

                    {/* Actions */}
                    {(period.status === 'pending' || period.status === 'validated') && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Notes (optionnel)
                          </label>
                          <textarea
                            value={validationNotes[period.id] || ''}
                            onChange={(e) => setValidationNotes(prev => ({ ...prev, [period.id]: e.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                            placeholder={period.status === 'pending' ? 'Notes de validation...' : 'Notes de paiement...'}
                          />
                        </div>
                        <div className="flex justify-end gap-3">
                          {period.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleCancel(period.id)}
                                disabled={actionLoading === period.id}
                                className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
                              >
                                <XCircle className="h-4 w-4 inline mr-2" />
                                Annuler
                              </button>
                              <button
                                onClick={() => handleValidate(period.id)}
                                disabled={actionLoading === period.id}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                              >
                                <CheckCircle className="h-4 w-4 inline mr-2" />
                                {actionLoading === period.id ? 'Validation...' : 'Valider'}
                              </button>
                            </>
                          )}
                          {period.status === 'validated' && (
                            <button
                              onClick={() => handlePay(period.id)}
                              disabled={actionLoading === period.id}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                            >
                              <Euro className="h-4 w-4 inline mr-2" />
                              {actionLoading === period.id ? 'Paiement...' : 'Marquer comme payée'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Validation/Payment Info */}
                    {period.validated_at && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm">
                        <p className="text-blue-800 dark:text-blue-200">
                          ✓ Validée le {new Date(period.validated_at).toLocaleDateString('fr-FR')}
                          {period.validation_notes && ` - ${period.validation_notes}`}
                        </p>
                      </div>
                    )}
                    {period.paid_at && (
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-sm">
                        <p className="text-green-800 dark:text-green-200">
                          💰 Payée le {new Date(period.paid_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
